import { useState } from 'react';
import { createFolder as createFolderApi, type IFolder } from '@apis/documents';
import { canCreateSubfolder } from '../utils/validation';

/**
 * The folder being created, before Genba has one.
 *
 * It has no id on purpose. Genba's ids are path-based and only exist once the
 * create returns, so any id invented here would be unusable upstream — and would
 * leak into everything that holds a folder id (the URL, query keys, drag-and-drop,
 * the upload target). Keeping it out of `folders` means none of those can go
 * wrong: this is a row on screen, not a folder.
 */
export interface INewFolder {
  parentId: string;
  name: string;
  status: 'naming' | 'creating';
  // Which surface opened it. Only that one renders the editor: two inputs for
  // the same folder means one of them blurs, and a blur with no name cancels.
  origin: 'tree' | 'table';
}

interface IUseNewFolderOptions {
  siteId: string | undefined;
  folders: IFolder[];
  onCreated: (folder: IFolder) => void;
  onError?: (error: unknown) => void;
}

export function useNewFolder({ siteId, folders, onCreated, onError }: IUseNewFolderOptions) {
  const [newFolder, setNewFolder] = useState<INewFolder | null>(null);

  // False when creation isn't allowed: the root level (folders live inside a
  // system root), the 10-level limit, or another create still in flight.
  function start(parentId: string | null, origin: INewFolder['origin']): boolean {
    if (parentId === null) return false;
    if (newFolder?.status === 'creating') return false;
    if (!canCreateSubfolder(folders, parentId)) return false;
    setNewFolder({ parentId, name: '', status: 'naming', origin });
    return true;
  }

  // Sends the create. The row stays on screen, inert, until it settles.
  async function commit(name: string): Promise<void> {
    if (newFolder?.status !== 'naming') return;
    const { parentId, origin } = newFolder;
    if (!siteId) { setNewFolder(null); return; }

    setNewFolder({ parentId, name, status: 'creating', origin });
    try {
      const { folder } = await createFolderApi(siteId, parentId, name);
      onCreated(folder);
      setNewFolder(null);
    } catch (err) {
      // Nothing reached the tree, so there's nothing to roll back.
      setNewFolder(null);
      onError?.(err);
    }
  }

  // Once the request is out it can't be called off — cancelling then would
  // create the folder upstream but never show it.
  function cancel(): void {
    if (newFolder?.status === 'creating') return;
    setNewFolder(null);
  }

  return { newFolder, start, commit, cancel };
}
